import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowUpRight, Calendar, Sparkles, Brush, Music, Award, Quote } from "lucide-react";

const PORTRAIT = "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/je7ytaat_SP058%20copy.jpg";

const COACHING_CALENDLY = "https://calendly.com/artofyou/begincreative";
const ART_THERAPY_CALENDLY = "https://calendly.com/artofyou/art-therapy-consultation";
const SOULSOUND_URL = "https://soulsound-alchemy.com";
const EMAIL = "contact@maria-katharina-richters.com";

export default function WorkWithMe() {
  useEffect(() => {
    document.title = "Work with me · Maria-Katharina Richters";
    const desc = "Coaching, art therapy, and sound healing with Maria-Katharina Richters. ICF-PCC certified, 2000+ hours of art therapy, 17 years on a spiritual path. Three doorways. Find the one calling.";
    const setMeta = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[property="og:title"]', 'content', 'Work with me · Maria-Katharina Richters');
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[name="twitter:description"]', 'content', desc);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div data-testid="work-with-me-page" className="min-h-screen bg-[#F7F5F2] text-[#1A1A1A] flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Hero />
        <CredentialsStrip />
        <SubNav />
        <WhoIAm />
        <WhyArt />
        <WhoThisIsFor />
        <TheBecoming />
        <ArtTherapy />
        <SoundHealing />
        <About />
        <Testimonials />
        <NotSure />
      </main>
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section data-testid="wwm-hero" className="relative border-b border-[#E5E0D8]">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#1A1A1A]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                Work with me
              </span>
            </div>
            <h1 className="font-display tracking-tighter leading-[0.96] text-[clamp(3rem,7.6vw,6.5rem)] font-medium text-[#1A1A1A]">
              Three doorways. <br />
              <span className="italic font-light text-[#1FA5B8]">Find the one calling.</span>
            </h1>
            <p className="mt-8 font-body text-[#1A1A1A] text-lg md:text-xl leading-[1.75] max-w-2xl">
              Coaching, art therapy, sound healing. Three modalities I have
              trusted in my own life and in the rooms I have held for others.
              Each one stands alone. Many people find the deepest shift comes
              when more than one is woven together.
            </p>
            <p className="mt-5 font-body text-[#5C5C5C] text-base leading-relaxed max-w-2xl">
              Read each pathway gently. The right one usually makes itself known.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={COACHING_CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wwm-hero-cta-consult"
                className="inline-flex items-center gap-2 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
              >
                <Calendar size={14} strokeWidth={1.6} />
                Book a free 15-min consult
              </a>
              <a
                href="#the-becoming"
                data-testid="wwm-hero-cta-becoming"
                className="inline-flex items-center gap-2 border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
              >
                See The Becoming
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          </div>

          <aside className="col-span-12 md:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] border border-[#E5E0D8] overflow-hidden bg-[#0A0A0A] shadow-[0_30px_60px_-30px_rgba(26,26,26,0.4)]">
                <img
                  src={PORTRAIT}
                  alt="Maria-Katharina Richters"
                  className="w-full h-full object-cover object-center block"
                  draggable="false"
                />
              </div>
              <div className="absolute -bottom-3 left-4 right-4 bg-[#F7F5F2] border border-[#E5E0D8] px-5 py-3 flex items-center justify-between">
                <span className="font-display italic text-[#1A1A1A] text-[15px] leading-none">
                  Maria-Katharina Richters
                </span>
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
                  Coach · Therapist · Sound healer
                </span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function CredentialsStrip() {
  const items = [
    { k: "ICF-PCC", v: "Coach Certification" },
    { k: "2000+ hrs", v: "Art Therapy (LOM & PM)" },
    { k: "7+ years", v: "Trauma & creative blocks" },
    { k: "17 years", v: "Spiritual development" },
    { k: "Kabbalah Centre", v: "Mentor" },
    { k: "Animas, 2017", v: "Coach trained" },
    { k: "SURU Music", v: "Wellness lead" },
  ];
  return (
    <section data-testid="wwm-credentials" className="bg-[#1A1A1A] text-[#F7F5F2] border-b border-[#1A1A1A]">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-5">
        <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {items.map((it, i) => (
            <li
              key={i}
              data-testid={`wwm-cred-${i + 1}`}
              className="flex items-baseline gap-2 font-body"
            >
              <span className="text-[#F7F5F2] text-[13px] font-medium tracking-tight">
                {it.k}
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#F7F5F2]/55">
                {it.v}
              </span>
              {i < items.length - 1 && (
                <span aria-hidden className="ml-6 text-[#F7F5F2]/25">·</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function SubNav() {
  const links = [
    { href: "#how-i-work", label: "How I work" },
    { href: "#who-this-is-for", label: "Who I meet" },
    { href: "#the-becoming", label: "The Becoming" },
    { href: "#art-therapy", label: "Art Therapy" },
    { href: "#sound", label: "Sound" },
    { href: "#about", label: "About" },
    { href: "#testimonials", label: "Voices" },
  ];
  return (
    <nav
      data-testid="wwm-subnav"
      className="sticky top-20 z-30 bg-[#F7F5F2]/85 backdrop-blur-xl border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <ul className="flex items-center gap-1 md:gap-2 overflow-x-auto py-3 -mx-1">
          {links.map((l) => (
            <li key={l.href} className="shrink-0">
              <a
                href={l.href}
                data-testid={`wwm-subnav-${l.href.replace("#", "")}`}
                className="inline-block px-3 md:px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#5C5C5C] hover:text-[#1FA5B8] font-body whitespace-nowrap transition-colors"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}


function WhoIAm() {
  return (
    <section id="how-i-work" data-testid="wwm-who" className="bg-[#FFFFFF] border-b border-[#E5E0D8] scroll-mt-32">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              How I work
            </div>
            <h2 className="mt-4 font-display tracking-tight leading-[1.05] text-[clamp(1.75rem,3.4vw,2.75rem)] font-medium">
              Coaching is the spine. <br />
              <span className="italic font-light">Art is always in the toolbox</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-8 space-y-5 font-body text-[#1A1A1A] text-[16px] md:text-[17px] leading-[1.85]">
            <p>
              Coaching, art therapy, and sound. I rarely use only one. The
              session you book may be named after one of the three, but in
              the room they weave. Coaching is constant. Art and sound layer
              in where they serve. A piece of paper and colour when words
              cannot yet reach what is asking to be said. A small sound bath
              when the body needs to land before we go any further.
            </p>
            <p>
              Underneath all of it sit seventeen years with the mystical
              teachings of Kabbalah. Not a religion. An ancient wisdom about
              receiving, about sharing from a full cup, and about catching
              the ego before it tricks you out of your own light. It gives
              me a working map of how the laws of creation move and how
              patterns form. So our work is never guessing. We stop fighting
              the current of your life. We learn to work with it.
            </p>
            <p>
              I name what I see calmly, with care, and directly. Compassion
              is the room. Truth is the furniture.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyArt() {
  return (
    <section data-testid="wwm-why-art" className="bg-[#F7F5F2] border-b border-[#E5E0D8]">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              Why art, always
            </div>
            <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.4vw,3.4rem)] font-medium">
              Words have a ceiling. <br />
              <span className="italic font-light">Colour does not</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 space-y-5 font-body text-[#1A1A1A] text-[16px] leading-[1.85]">
            <p>
              I am trained in Solution-Oriented Painting (LOM), a trauma
              modality as effective as EMDR. Painting bypasses logic and
              reaches the parts of the brain and body that hold what words
              cannot. The body keeps the score. Art is how it tells the
              story.
            </p>
            <p className="text-[#5C5C5C]">
              You do not have to be an artist. You do not have to make
              anything beautiful. The process reveals what is ready. That
              is the part that changes you.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoThisIsFor() {
  const archetypes = [
    {
      key: "producer",
      label: "The music producer",
      from: "The session feels mechanical. The sound you used to chase is somewhere behind a fog.",
      to: "A voice in your work again. The kind of track only you could have made.",
      badge: "Wellness Lead at SURU Music",
    },
    {
      key: "painter",
      label: "The painter or visual artist",
      from: "The canvas waits. You scroll. You clean the studio instead of using it.",
      to: "A practice you actually return to. Daily, unforced, with hands that remember.",
      badge: null,
    },
    {
      key: "escapee",
      label: "The corporate creative",
      from: "You sit in the 9-to-5. Something quiet inside keeps tapping at the glass.",
      to: "A first creative life, built slowly around what lights you up. No reckless burning down.",
      badge: null,
    },
  ];

  return (
    <section id="who-this-is-for" data-testid="wwm-archetypes" className="bg-[#FFFFFF] border-b border-[#E5E0D8] scroll-mt-32">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-8 items-end mb-14">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              Who I keep meeting
            </div>
            <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.6vw,3.6rem)] font-medium">
              Three lives I work with often. <br />
              <span className="italic font-light">Maybe one is yours</span>.
            </h2>
          </div>
        </div>

        <ul className="grid grid-cols-12 gap-6 md:gap-8">
          {archetypes.map((a, i) => (
            <li
              key={a.key}
              data-testid={`wwm-archetype-${a.key}`}
              className="col-span-12 md:col-span-4 border border-[#E5E0D8] bg-[#F7F5F2] p-7 md:p-8 flex flex-col"
            >
              <div className="flex items-baseline justify-between mb-8">
                <span className="font-display text-[28px] leading-none tracking-tight text-[#1FA5B8]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] font-body">
                  Archetype
                </span>
              </div>

              <h3 className="font-display text-[#1A1A1A] text-[24px] leading-tight tracking-tight mb-2">
                {a.label}
              </h3>
              {a.badge && (
                <div className="mb-7 inline-flex items-center gap-1.5">
                  <Award size={11} strokeWidth={1.6} className="text-[#1FA5B8]" />
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#1FA5B8] font-body">
                    {a.badge}
                  </span>
                </div>
              )}
              {!a.badge && <div className="mb-7" />}

              <div className="space-y-5 flex-1">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] font-body mb-2">
                    From
                  </div>
                  <p className="font-body text-[#1A1A1A] text-[15px] leading-[1.7]">
                    {a.from}
                  </p>
                </div>
                <div className="h-px w-10 bg-[#1FA5B8]" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body mb-2">
                    To
                  </div>
                  <p className="font-body text-[#1A1A1A] text-[15px] leading-[1.7] italic">
                    {a.to}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function TheBecoming() {
  return (
    <section
      id="the-becoming"
      data-testid="wwm-becoming"
      className="bg-[#1A1A1A] text-[#F7F5F2] relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(45% 60% at 80% 20%, rgba(31,165,184,0.30) 0%, rgba(26,26,26,0) 70%), radial-gradient(45% 50% at 15% 85%, rgba(31,165,184,0.15) 0%, rgba(26,26,26,0) 70%)",
        }}
      />
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 mb-12 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <Sparkles size={14} strokeWidth={1.4} className="text-[#1FA5B8]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#1FA5B8] font-body">
                The flagship container
              </span>
              <span className="border border-[#1FA5B8] text-[#1FA5B8] font-body text-[10px] uppercase tracking-[0.28em] px-2.5 py-1 leading-none">
                Signature Programme
              </span>
            </div>
            <h2 className="font-display leading-[1] tracking-tight text-[clamp(2.5rem,5.6vw,5rem)] font-medium">
              The Becoming. <br />
              <span className="italic font-light">Twelve weeks. Three modalities. One you.</span>
            </h2>
            <p className="mt-6 font-display italic text-[#F7F5F2]/85 text-[clamp(1.3rem,2.2vw,1.8rem)] leading-tight">
              Not a stack of sessions. A held thread.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F7F5F2]/55 font-body mb-2">
              Investment from
            </div>
            <div className="font-display text-[#F7F5F2] text-[40px] leading-none">
              EUR 1,800
            </div>
            <div className="mt-2 text-[12px] text-[#F7F5F2]/60 font-body">
              Three ways to pay. See below.
            </div>
          </div>
        </div>

        <p className="font-body text-[#F7F5F2]/85 text-lg md:text-xl leading-[1.8] max-w-3xl">
          A twelve-week one-to-one container for the moment you know you are
          ready to step into a new self. Not a step-by-step program. You
          bring the outcome you are reaching toward, and I walk with you,
          intuitively, using whatever each turn asks for.
        </p>

        {/* The process — pull quote + beats */}
        <div className="mt-14 grid grid-cols-12 gap-x-8 gap-y-6 items-start">
          <div className="col-span-12 md:col-span-5">
            <blockquote className="border-l-2 border-[#1FA5B8] pl-6 py-2">
              <p className="font-display italic text-[#F7F5F2] text-[clamp(1.4rem,2.4vw,1.9rem)] leading-[1.25] tracking-tight">
                The outcome you arrive with is the{" "}
                <span className="text-[#1FA5B8]">doorway</span>, not the destination.
              </p>
            </blockquote>
          </div>
          <div className="col-span-12 md:col-span-7 space-y-4 font-body text-[#F7F5F2]/75 text-[16px] leading-[1.8]">
            <p>
              As we work, layers begin to fall away. Patterns you carried
              because they were given to you. Dreams you are finally
              letting go because you realise they were never quite yours.
              Or dreams you have carried quietly for years that you are
              now ready to walk toward.
            </p>
            <p>
              What you thought you wanted often shifts. What reveals itself
              underneath is closer to what your life has been pointing at
              all along. Often simpler.{" "}
              <span className="text-[#F7F5F2]">
                Often the relief you did not know you were waiting for.
              </span>
            </p>
          </div>
        </div>

        {/* Soft divider */}
        <div className="mt-12 mb-12 h-px w-16 bg-[#F7F5F2]/20" aria-hidden />

        {/* Alignment — pull quote + beats */}
        <div className="grid grid-cols-12 gap-x-8 gap-y-6 items-start">
          <div className="col-span-12 md:col-span-5">
            <blockquote className="border-l-2 border-[#1FA5B8] pl-6 py-2">
              <p className="font-display italic text-[#F7F5F2] text-[clamp(1.4rem,2.4vw,1.9rem)] leading-[1.25] tracking-tight">
                Real change does not arrive from will alone. It needs{" "}
                <span className="text-[#1FA5B8]">alignment</span>.
              </p>
            </blockquote>
          </div>
          <div className="col-span-12 md:col-span-7 space-y-4 font-body text-[#F7F5F2]/75 text-[16px] leading-[1.8]">
            <p>
              Thoughts, words, daily actions, the ordinary hours of an
              ordinary Tuesday. All moving in the same direction as the
              vision you carry.
            </p>
            <p>
              This is not usually work you can do alone. The mind is too
              close to itself to see its own loops. You need someone to
              witness them, name them gently, and walk beside you while you
              make the leap, whether the leap is loud or so quiet only you
              and one other person will ever know it happened.
            </p>
          </div>
        </div>

        <p className="mt-12 font-display italic text-[#F7F5F2] text-[22px] md:text-[26px] leading-[1.35] max-w-3xl">
          My only job is to help you become who you already are.
        </p>

        <ul className="mt-12 grid grid-cols-12 gap-x-8 gap-y-8 font-body text-[#F7F5F2]/80 text-[15px] leading-[1.75]">
          <li className="col-span-12 md:col-span-6 border-t border-[#F7F5F2]/15 pt-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body mb-2">01</div>
            <p className="text-[#F7F5F2] font-display text-[20px] leading-tight mb-2">
              Held across all twelve weeks.
            </p>
            <p>
              In single sessions, the work resets each time you book. In the
              container, I hold the thread of what is moving in you between
              meetings, track the patterns as they shift, and bring the right
              next piece into our following session. You are never starting
              from zero.
            </p>
          </li>
          <li className="col-span-12 md:col-span-6 border-t border-[#F7F5F2]/15 pt-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body mb-2">02</div>
            <p className="text-[#F7F5F2] font-display text-[20px] leading-tight mb-2">
              Voice notes when something moves.
            </p>
            <p>
              Between sessions you can send a voice note. I respond
              personally. Not 24/7, not endless. But you are never left
              alone with a breakthrough, a worry, or a wobble in the middle
              of your week.
            </p>
          </li>
          <li className="col-span-12 md:col-span-6 border-t border-[#F7F5F2]/15 pt-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body mb-2">03</div>
            <p className="text-[#F7F5F2] font-display text-[20px] leading-tight mb-2">
              Coaching and art every session. Sound when the body asks.
            </p>
            <p>
              Every session weaves a coaching frame with a hands-on creative
              piece. Sound layers in when the body needs to land first. You
              are not booking one modality. You are getting all of me, in
              the order each week needs.
            </p>
          </li>
          <li className="col-span-12 md:col-span-6 border-t border-[#F7F5F2]/15 pt-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body mb-2">04</div>
            <p className="text-[#F7F5F2] font-display text-[20px] leading-tight mb-2">
              A real beginning, middle, and end.
            </p>
            <p>
              We name the outcome you are walking toward on day one. We
              close with a session that recognises what has actually
              changed and helps it settle into you. Without that arc,
              transformation can fade. With it, the new you stays.
            </p>
          </li>
        </ul>

        {/* What shifts — outcomes block */}
        <div className="mt-20 pt-10 border-t border-[#F7F5F2]/15">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#1FA5B8] font-body mb-4">
            What people arrive with. What shifts.
          </div>
          <h3 className="font-display text-[#F7F5F2] text-[clamp(1.75rem,3.4vw,2.6rem)] leading-[1.1] tracking-tight max-w-3xl mb-12">
            The work people walk in carrying.{" "}
            <span className="italic font-light">What they walk out into.</span>
          </h3>

          {/* Header row (desktop only) */}
          <div className="hidden md:grid grid-cols-12 gap-x-6 pb-3 border-b border-[#F7F5F2]/15 mb-3">
            <div className="col-span-5 text-[10px] uppercase tracking-[0.3em] text-[#F7F5F2]/45 font-body">
              Arriving with
            </div>
            <div className="col-span-7 text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
              After
            </div>
          </div>

          <ul data-testid="wwm-becoming-shifts">
            {[
              {
                from: "Stuck. Overthinking everything. Creating from anxiety instead of instinct.",
                to: "Clear. Quieter inside. Creating from instinct again, with momentum.",
              },
              {
                from: "An inner critic running the show. Fear of being seen, or even of succeeding.",
                to: "The critic still speaks. You stop obeying it. Visibility stops costing you.",
              },
              {
                from: "A vision that feels far away from your actual Tuesday.",
                to: "Your daily life starts moving in the same direction as the vision.",
              },
              {
                from: "Tired, foggy, depleted. The energy you need is somewhere you cannot reach.",
                to: "Energy returns. The body feels held. Stamina for the work comes back.",
              },
              {
                from: "Old grief, fear, or anger lodged in the body. Patterns that keep repeating.",
                to: "The pattern is named. The charge moves through. The loop quiets.",
              },
              {
                from: "A quiet suspicion that the dream you carry is not entirely yours.",
                to: "Honest clarity about what is yours, and the relief of a true pivot if it is needed.",
              },
            ].map((s, i) => (
              <li
                key={i}
                data-testid={`wwm-shift-${i + 1}`}
                className="grid grid-cols-12 gap-x-6 gap-y-2 py-5 border-b border-[#F7F5F2]/10 last:border-0"
              >
                <p className="col-span-12 md:col-span-5 font-body text-[#F7F5F2]/60 text-[15px] leading-[1.65]">
                  {s.from}
                </p>
                <p className="col-span-12 md:col-span-7 font-body text-[#F7F5F2] text-[15px] leading-[1.65] italic">
                  {s.to}
                </p>
              </li>
            ))}
          </ul>

          <p className="mt-10 font-body text-[#F7F5F2]/60 text-[13px] leading-relaxed italic max-w-3xl">
            Not promises. Patterns I see in the people who choose this work and stay with it.
          </p>
        </div>

        <div className="mt-20 pt-12 border-t border-[#F7F5F2]/15">
          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 md:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.32em] text-[#1FA5B8] font-body">
                Investment
              </div>
              <h3 className="mt-4 font-display text-[#F7F5F2] leading-tight tracking-tight text-[clamp(1.8rem,3.5vw,2.5rem)] font-medium">
                Three months, <span className="italic font-light">three</span> ways.
              </h3>
              <p className="mt-5 font-body text-[#F7F5F2]/60 text-sm leading-relaxed max-w-xs">
                Paying upfront is the simplest. Instalments are there if you
                need them, with a small uplift to keep the longer commitment
                fair on both sides.
              </p>
            </div>

            <ul className="col-span-12 md:col-span-7 md:col-start-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  tag: "Upfront",
                  amount: "\u20AC1800",
                  detail: "Paid at the start of the container",
                  highlight: true,
                },
                {
                  tag: "In two",
                  amount: "2 \u00D7 \u20AC975",
                  detail: "Half at start, half at midpoint",
                },
                {
                  tag: "Monthly",
                  amount: "3 \u00D7 \u20AC700",
                  detail: "Across the three months",
                },
              ].map((opt, i) => (
                <li
                  key={i}
                  data-testid={`wwm-becoming-price-${i + 1}`}
                  className={`border p-6 flex flex-col ${
                    opt.highlight
                      ? "border-[#1FA5B8] bg-[#1FA5B8]/10"
                      : "border-[#F7F5F2]/20"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
                    {opt.tag}
                  </div>
                  <div className="mt-3 font-display text-[#F7F5F2] text-[28px] leading-none tracking-tight">
                    {opt.amount}
                  </div>
                  <p className="mt-3 font-body text-[#F7F5F2]/60 text-xs leading-relaxed">
                    {opt.detail}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
          <a
            href={COACHING_CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wwm-becoming-cta"
            className="inline-flex items-center gap-2 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-12 px-6 transition-colors"
          >
            <Calendar size={14} strokeWidth={1.6} />
            Book a free 15-min Begin Again call
          </a>
          <a
            href={`mailto:${EMAIL}?subject=The%20Becoming`}
            data-testid="wwm-becoming-email"
            className="inline-flex items-center gap-2 border border-[#F7F5F2]/40 hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-12 px-6 transition-colors"
          >
            Ask a question first
            <ArrowUpRight size={14} strokeWidth={1.6} />
          </a>
          <p className="font-body text-[12px] text-[#F7F5F2]/50">
            No pressure. We&apos;ll see if this is the right fit, together.
          </p>
        </div>
      </div>
    </section>
  );
}

function ArtTherapy() {
  return (
    <section id="art-therapy" data-testid="wwm-art-therapy" className="bg-[#F7F5F2] border-b border-[#E5E0D8] scroll-mt-32">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <Brush size={14} strokeWidth={1.4} className="text-[#1FA5B8]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#1FA5B8] font-body">
                Pathway · Art therapy
              </span>
            </div>
            <h2 className="font-display tracking-tight leading-[1.05] text-[clamp(2rem,4.4vw,3.4rem)] font-medium">
              Where words <br />
              <span className="italic font-light">do not reach</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 font-body text-[#1A1A1A] text-[16px] leading-[1.85] space-y-5">
            <p>
              Art therapy is about <span className="italic">release</span>.
              The emotions that have been waiting too long. The looping
              grief, the silent fear, the unspoken anger. When they stay
              stuck, they begin to attack the body. Organs, sleep, mood.
              From there they shape what you create, and what life keeps
              bringing back to you.
            </p>
            <p>
              The work is letting it go before it metastasises. I take the
              pain seriously. I do not bypass it or call it imagined. I sit
              with what is real, then I show you the way out of the loop.
              A spiritual lens and a therapist&apos;s training, together.
              Both, not one without the other.
            </p>

            <div className="pt-4 flex flex-wrap gap-3 items-center">
              <a
                href={ART_THERAPY_CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wwm-art-cta"
                className="inline-flex items-center gap-2 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-10 px-4 transition-colors"
              >
                <Calendar size={13} strokeWidth={1.6} />
                Book a consultation
              </a>
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                Single session or pack
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SoundHealing() {
  return (
    <section id="sound" data-testid="wwm-sound" className="bg-[#FFFFFF] border-b border-[#E5E0D8] scroll-mt-32">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <Music size={14} strokeWidth={1.4} className="text-[#1FA5B8]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#1FA5B8] font-body">
                Pathway · Sound healing
              </span>
            </div>
            <h2 className="font-display tracking-tight leading-[1.05] text-[clamp(2rem,4.4vw,3.4rem)] font-medium">
              For the body that <br />
              <span className="italic font-light">listens first</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 font-body text-[#1A1A1A] text-[16px] leading-[1.85] space-y-5">
            <p>
              Sound is <span className="italic">frequency</span>. It moves
              stuck energy in the body, and whatever you are ready to release
              begins to release. Cells re-attune to harmony. The body
              remembers how to heal itself. Sound is only there to help it
              return to the state where it can do what it already knows how
              to do.
            </p>
            <p>
              Sound is woven through The Becoming, and it also stands alone.
              One-to-one sound journeys or small group sound baths. Most
              powerful in person, in my studio in Jávea, Spain. Also
              available online when distance asks for it.
            </p>

            <div className="pt-4 flex flex-wrap gap-3 items-center">
              <a
                href={SOULSOUND_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wwm-sound-cta"
                className="inline-flex items-center gap-2 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-10 px-4 transition-colors"
              >
                Visit SoulSound
                <ArrowUpRight size={13} strokeWidth={1.6} />
              </a>
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                Jávea · Online · Group
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function About() {
  const credentials = [
    "ICF-PCC accredited coach (the gold standard)",
    "Animas Centre for Coaching trained, 2017",
    "2000+ hours of art therapy practice (LOM & PM)",
    "7+ years working with trauma, anxiety, PTSD, addiction",
    "17 years on a dedicated spiritual development path",
    "Mentor at the Kabbalah Centre",
    "Coach Supervisor, in ongoing supervision in both fields",
    "Wellness & Communications Lead, SURU Music",
    "DJ & producer (MAKARII), host of Born to Create",
  ];

  const lineage = [
    { name: "Nancy Kline", note: "the power of being heard" },
    { name: "Bessel van der Kolk", note: "the body keeps the score" },
    { name: "Norman Doidge", note: "neuroplasticity" },
    { name: "Bruce Lipton", note: "epigenetics of perception" },
    { name: "Joe Dispenza", note: "states of the nervous system" },
    { name: "Bettina Egger", note: "solution-oriented painting" },
  ];

  return (
    <section
      id="about"
      data-testid="wwm-about"
      className="bg-[#F7F5F2] border-b border-[#E5E0D8] scroll-mt-32"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-12 gap-8 items-end mb-16">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              About me
            </div>
            <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.6vw,3.6rem)] font-medium">
              Trained deeply, <br />
              <span className="italic font-light">held in supervision</span>.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-x-10 gap-y-12">
          {/* Bio short */}
          <div className="col-span-12 md:col-span-7 font-body text-[#1A1A1A] text-[16px] leading-[1.85] space-y-4">
            <p>
              I am a Transformational Art Coach, art therapist, sound healer,
              and artist. I started my coaching practice Art of You in 2017
              and have spent the last eight years working with artists,
              musicians, and quiet creatives moving through patterns that
              once held them back.
            </p>
            <p>
              My understanding of the subconscious began in 2009 through the
              mystical teachings of Kabbalah. My clinical training came
              after, in art therapy and ICF-accredited coaching. I work
              under supervision in both, and I supervise other coaches too.
            </p>
            <p className="text-[#5C5C5C]">
              I sit in Jávea, Spain, and work in person and online with
              clients around the world.
            </p>
          </div>

          {/* Credentials list */}
          <div className="col-span-12 md:col-span-5">
            <div className="border border-[#E5E0D8] bg-[#FFFFFF] p-7 md:p-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body mb-5">
                Credentials
              </div>
              <ul className="space-y-3.5">
                {credentials.map((c, i) => (
                  <li
                    key={i}
                    className="flex gap-3 font-body text-[14px] text-[#1A1A1A] leading-[1.55]"
                  >
                    <span className="text-[#1FA5B8] shrink-0 mt-1.5">·</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Lineage */}
          <div className="col-span-12">
            <div className="border-t border-[#E5E0D8] pt-10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] font-body mb-6">
                The work my work rests on
              </div>
              <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-5">
                {lineage.map((p, i) => (
                  <li
                    key={i}
                    data-testid={`wwm-lineage-${i + 1}`}
                    className="font-body"
                  >
                    <div className="font-display text-[#1A1A1A] text-[15px] leading-tight">
                      {p.name}
                    </div>
                    <div className="text-[11px] text-[#5C5C5C] italic mt-1 leading-snug">
                      {p.note}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function Testimonials() {
  const items = [
    {
      quote:
        "Eight months ago I had no clue about my potential. Today I know it. Thank you Maria and Art of You.",
      name: "Helma",
      role: "Client",
      featured: true,
    },
    {
      quote:
        "In only four sessions I was able to move forward from a financial issue that has plagued me for years, literally.",
      name: "Maureen",
      role: "Trader, London",
    },
    {
      quote:
        "After only three sessions I was able to become pregnant, after years of trying.",
      name: "Claudia",
      role: "Client",
    },
    {
      quote:
        "After one session I was able to overcome insomnia that had plagued me for years. From not knowing myself, I now know what I want to create and I am going for it.",
      name: "Teo",
      role: "Client",
    },
    {
      quote:
        "Working with Maria has been an experience of real growth. I am now close to finishing my first body of work. She helped me develop my confidence as an artist and create structures for approaching selling my work.",
      name: "Farouk Alao",
      role: "Multidisciplinary Artist, Ireland",
    },
    {
      quote:
        "I wasn't feeling connected to my creativity anymore. I was stuck. After three sessions something clicked. I've created two tracks in two weeks. I feel full of energy. Like a new person.",
      name: "Biggi",
      role: "Music Producer",
      featured: true,
    },
    {
      quote:
        "Maria is such an empathic coach. I felt completely free to explore what I was working through and not once did I feel judged. Her insights were often lightbulb moments for me as a coachee.",
      name: "Claire Husselbee",
      role: "Musician & Transformational Coach",
    },
  ];

  return (
    <section
      id="testimonials"
      data-testid="wwm-testimonials"
      className="bg-[#FFFFFF] border-b border-[#E5E0D8] scroll-mt-32"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end mb-16">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              In their own words
            </div>
            <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.6vw,3.6rem)] font-medium">
              After working with me. <br />
              <span className="italic font-light">In their words, not mine</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] font-body">
              From the Art of You archive
            </span>
          </div>
        </div>

        {/* Featured video testimonials */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#1FA5B8]" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#1FA5B8] font-body">
              Video · Client stories
            </span>
          </div>
          <div className="grid grid-cols-12 gap-6 md:gap-8">
            {[
              {
                src: "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/zwp47igh_Miguel%20%26%20Iyvonne%20Client%20Testimonial.mp4",
                name: "Miguel & Iyvonne",
                line: "in their own voices",
                blurb:
                  "A short conversation about what shifted, in their own time and on their own terms.",
                testId: "wwm-video-miguel-iyvonne",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/sgxkjiz9_Client%20Testimonial%20Tara.mp4",
                name: "Tara",
                line: "what the work moved",
                blurb:
                  "A quiet reflection on what the work made possible. Press play when you have a quiet moment.",
                testId: "wwm-video-tara",
              },
            ].map((v, i) => (
              <article
                key={i}
                data-testid={v.testId}
                className="col-span-12 md:col-span-6 flex flex-col"
              >
                <div className="relative aspect-video bg-[#0A0A0A] border border-[#E5E0D8] overflow-hidden shadow-[0_18px_40px_-20px_rgba(26,26,26,0.35)]">
                  <video
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full h-full object-cover block"
                    src={v.src}
                  >
                    Sorry, your browser does not support embedded video.
                  </video>
                </div>
                <div className="mt-5">
                  <h3 className="font-display text-[#1A1A1A] text-[22px] md:text-[24px] leading-[1.15] tracking-tight">
                    {v.name},{" "}
                    <span className="italic font-light">{v.line}</span>.
                  </h3>
                  <p className="mt-3 font-body text-[#5C5C5C] text-[14px] leading-relaxed max-w-md">
                    {v.blurb}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        <ul className="grid grid-cols-12 gap-6 md:gap-8">
          {items.map((t, i) => (
            <li
              key={i}
              data-testid={`wwm-testimonial-${i + 1}`}
              className={`${
                t.featured
                  ? "col-span-12 md:col-span-8 bg-[#1A1A1A] text-[#F7F5F2] border border-[#1A1A1A]"
                  : "col-span-12 md:col-span-4 bg-[#F7F5F2] text-[#1A1A1A] border border-[#E5E0D8]"
              } p-7 md:p-8 flex flex-col`}
            >
              <Quote
                size={20}
                strokeWidth={1.4}
                className={`${
                  t.featured ? "text-[#1FA5B8]" : "text-[#1FA5B8]"
                } mb-5`}
              />
              <blockquote
                className={`font-body leading-[1.65] flex-1 ${
                  t.featured
                    ? "text-[18px] md:text-[20px] text-[#F7F5F2]/90 font-display italic leading-[1.4]"
                    : "text-[14px] text-[#1A1A1A]/85"
                }`}
              >
                {t.quote}
              </blockquote>
              <footer className="mt-6 pt-4 border-t border-current/15 flex items-baseline justify-between gap-3 flex-wrap">
                <span
                  className={`font-display ${
                    t.featured
                      ? "text-[18px] text-[#F7F5F2]"
                      : "text-[15px] text-[#1A1A1A]"
                  } leading-tight`}
                >
                  {t.name}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-[0.28em] font-body ${
                    t.featured ? "text-[#1FA5B8]" : "text-[#5C5C5C]"
                  }`}
                >
                  {t.role}
                </span>
              </footer>
            </li>
          ))}
        </ul>

        <p className="mt-12 font-body text-[13px] text-[#5C5C5C] italic max-w-2xl">
          Excerpts. Full testimonials and many more live on
          maria-katharina-richters.com.
        </p>
      </div>
    </section>
  );
}

function NotSure() {
  return (
    <section data-testid="wwm-not-sure" className="bg-[#F7F5F2]">
      <div className="max-w-[920px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="h-px w-8 bg-[#1FA5B8]" />
          <span className="text-[11px] uppercase tracking-[0.32em] text-[#1FA5B8] font-body">
            Not sure which door?
          </span>
          <span className="h-px w-8 bg-[#1FA5B8]" />
        </div>
        <h2 className="font-display leading-[1.05] tracking-tight text-[clamp(2rem,4.6vw,3.6rem)] font-medium">
          Tell me where you are. <br />
          <span className="italic font-light">I will help you find it</span>.
        </h2>
        <p className="mt-6 font-body text-[#1A1A1A] text-lg leading-[1.8] max-w-2xl mx-auto">
          Book a free 15-minute conversation and we will look together at
          which pathway fits where you are right now. No pressure, no
          pitching. Just clarity.
        </p>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a
            href={COACHING_CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wwm-not-sure-cta"
            className="inline-flex items-center gap-2 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
          >
            <Calendar size={14} strokeWidth={1.6} />
            Book the 15-minute call
          </a>
          <a
            href={`mailto:${EMAIL}?subject=Work%20with%20me`}
            data-testid="wwm-not-sure-email"
            className="inline-flex items-center gap-2 border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
          >
            Or hit reply
            <ArrowUpRight size={14} strokeWidth={1.6} />
          </a>
        </div>
      </div>
    </section>
  );
}
